'use client';

// ═══════════════════════════════════════════════════════════════
// Currency Context & Hook
// Manages display-only currency conversion for the storefront
// ═══════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import {
    convertCurrency,
    detectCurrencyFromLocale,
    CURRENCY_FLAGS,
    CURRENCY_NAMES_AR,
    EXCHANGE_RATES,
} from '../exchange-rates';
import { getCurrencyCode, formatPrice } from '@/themes/bold-eco-v1/lib/currency';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface CurrencyContextValue {
    /** Store's base currency code (e.g., 'DZD') */
    baseCurrency: string;
    /** Visitor's chosen display currency code */
    selectedCurrency: string;
    /** List of currencies enabled by the store owner */
    enabledCurrencies: string[];
    /** Whether to show the currency selector in the UI */
    showSelector: boolean;
    /** Change the display currency */
    setSelectedCurrency: (currency: string) => void;
    /** Convert amount from base → selected currency */
    convertPrice: (amount: number) => number;
    /** Format a price in the selected display currency with ≈ prefix */
    formatDisplayPrice: (amount: number) => string;
    /** Format a price in the store's base currency without prefix */
    formatBasePrice: (amount: number) => string;
    /** Whether a conversion is active (selected !== base) */
    isConverted: boolean;
}

interface CurrencyProviderProps {
    children: ReactNode;
    baseCurrency?: string;
    enabledCurrencies?: string[];
    autoDetect?: boolean;
}

// ─────────────────────────────────────────────────────────────
// Cookie helpers
// ─────────────────────────────────────────────────────────────

const COOKIE_NAME = 'ecommaps_display_currency';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1] ?? '') : null;
}

function setCookie(name: string, value: string) {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
}

// ─────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────

export function CurrencyProvider({
    children,
    baseCurrency: baseCurrencyProp = 'DZD',
    enabledCurrencies: enabledCurrenciesProp = [],
    autoDetect = false,
}: CurrencyProviderProps) {
    // Normalize base currency to ISO code
    const baseCurrency = getCurrencyCode(baseCurrencyProp);

    // Ensure enabled list always includes the base currency
    const enabledCurrencies = useMemo(() => {
        const codes = enabledCurrenciesProp.map(c => getCurrencyCode(c));
        if (!codes.includes(baseCurrency)) {
            return [baseCurrency, ...codes];
        }
        return codes;
    }, [enabledCurrenciesProp, baseCurrency]);

    // Show selector only when there's more than 1 enabled currency
    const showSelector = enabledCurrencies.length > 1;

    // State
    const [selectedCurrency, setSelectedCurrencyState] = useState<string>(baseCurrency);

    // Initialize from cookie or auto-detect on mount
    useEffect(() => {
        // 1. Check cookie first
        const cookieValue = getCookie(COOKIE_NAME);
        if (cookieValue && enabledCurrencies.includes(cookieValue)) {
            setSelectedCurrencyState(cookieValue);
            return;
        }

        // 2. Auto-detect from browser locale
        if (autoDetect && showSelector) {
            const detected = detectCurrencyFromLocale();
            if (detected && enabledCurrencies.includes(detected)) {
                setSelectedCurrencyState(detected);
                setCookie(COOKIE_NAME, detected);
                return;
            }
        }

        // 3. Default to base currency
        setSelectedCurrencyState(baseCurrency);
    }, [baseCurrency, enabledCurrencies, autoDetect, showSelector]);

    // Set currency and persist to cookie
    const setSelectedCurrency = useCallback((currency: string) => {
        const code = getCurrencyCode(currency);
        if (enabledCurrencies.includes(code)) {
            setSelectedCurrencyState(code);
            setCookie(COOKIE_NAME, code);
        }
    }, [enabledCurrencies]);

    // Convert price from base to selected
    const convertPrice = useCallback((amount: number): number => {
        return convertCurrency(amount, baseCurrency, selectedCurrency);
    }, [baseCurrency, selectedCurrency]);

    // Format price in the display currency with ≈ prefix
    const formatDisplayPrice = useCallback((amount: number): string => {
        if (baseCurrency === selectedCurrency) {
            return formatPrice(amount, baseCurrency);
        }
        const converted = convertCurrency(amount, baseCurrency, selectedCurrency);
        return `≈ ${formatPrice(converted, selectedCurrency)}`;
    }, [baseCurrency, selectedCurrency]);

    // Format price in the base currency
    const formatBasePrice = useCallback((amount: number): string => {
        return formatPrice(amount, baseCurrency);
    }, [baseCurrency]);

    const isConverted = baseCurrency !== selectedCurrency;

    const value = useMemo<CurrencyContextValue>(() => ({
        baseCurrency,
        selectedCurrency,
        enabledCurrencies,
        showSelector,
        setSelectedCurrency,
        convertPrice,
        formatDisplayPrice,
        formatBasePrice,
        isConverted,
    }), [
        baseCurrency, selectedCurrency, enabledCurrencies, showSelector,
        setSelectedCurrency, convertPrice, formatDisplayPrice, formatBasePrice, isConverted,
    ]);

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
}

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

/**
 * Access currency conversion context
 * Must be used within a CurrencyProvider
 */
export function useCurrency(): CurrencyContextValue {
    const ctx = useContext(CurrencyContext);
    if (!ctx) {
        // Graceful fallback — return DZD defaults when not in a provider
        return {
            baseCurrency: 'DZD',
            selectedCurrency: 'DZD',
            enabledCurrencies: ['DZD'],
            showSelector: false,
            setSelectedCurrency: () => { },
            convertPrice: (amount: number) => amount,
            formatDisplayPrice: (amount: number) => formatPrice(amount, 'DZD'),
            formatBasePrice: (amount: number) => formatPrice(amount, 'DZD'),
            isConverted: false,
        };
    }
    return ctx;
}

// Re-export useful helpers
export { CURRENCY_FLAGS, CURRENCY_NAMES_AR, EXCHANGE_RATES };
